import { ascending } from 'd3-array'
import moment from 'moment'

import { listService } from '#base/js/common/services/list.service'
import { dataFilterService } from '../data-filter.service'
import { mapFromRawData } from '../table/table-mapping'
import { lineFormatService } from './line-format.service'
import { linePropService } from './line-prop.service'

class LineDataService {
  /**
   * Sort the raw data lists
   */
  sortRawData (rawData, sortField) {
    return rawData.map((data) => {
      if (Array.isArray(data)) {
        data = data.sort((a, b) => ascending(a[sortField], b[sortField]))
      }
      return data
    })
  }

  removeEmptyValues (rawData, valueField) {
    return rawData.map((data) => {
      return data.filter((d) => d[valueField] !== undefined && d[valueField] !== null && d[valueField] !== 'NaN')
    })
  }

  /**
   * Orders the chart series or table column list in place based on the current legend config
   */
  sortSeries (list, legend) {
    if (!legend) {
      return
    }

    const order = legend.map(({ value }) => value)
    listService.sortObjectList(list, 'name', order)
  }

  /**
   * Get the min and max values from the list of raw data lists sorted by the valueField
   */
  getMinMaxSorted (sortedRawData, valueField) {
    const result = sortedRawData.reduce((result, data) => {
      const distinctValues = dataFilterService.getDistinctValues(data, [ valueField ])
      const sortedDistinctValues = distinctValues[0].values
      if (sortedDistinctValues?.length) {
        const min = sortedDistinctValues[0]
        if (result.min === undefined || min < result.min) {
          result.min = min
        }
        const max = sortedDistinctValues[sortedDistinctValues.length - 1]
        if (result.max === undefined || max > result.max) {
          result.max = max
        }
      }

      return result
    }, {})

    if (Object.keys(result).length === 2) {
      return result
    }
  }

  /**
   * Get the min and max values from the list of raw data lists that are not sorted by the valueField
   */
  getMinMaxUnsorted (rawData, valueFields) {
    const result = rawData.reduce((result, data) => {
      data.forEach((point) => {
        valueFields.forEach((valueField) => {
          if (valueField === undefined) {
            return
          }
          const value = point[valueField]
          if (value === undefined || value === null) {
            return
          }
          if (result.min === undefined || value < result.min) {
            result.min = value
          }
          if (result.max === undefined || value > result.max) {
            result.max = value
          }
        })
      })
      return result
    }, {})

    if (Object.keys(result).length === 2) {
      return result
    }
  }

  /**
   * Map from raw data into the format required by the line chart
   */
  toChartData (data, dataDefinition, strata, scaleTypeX) {
    const { xField, yField, y2Field, yLoField, yHiField } = dataDefinition

    const series = data.reduce((series, r) => {
      // Find series for this data point and add if doesn't exist
      const seriesId = `${strata.value}_${r[strata.value]}`
      const name = r[strata.value] || 'Overall'
      let s = series.find(({ id }) => id === seriesId)
      if (!s) {
        s = {
          id: seriesId,
          name,
          points: []
        }
        series.push(s)
      }

      // Set point values
      let point = {
        x: r[xField],
        y: r[yField]
      }
      if (y2Field) {
        point.y2 = r[y2Field]
      }
      if (yLoField) {
        point.yLo = r[yLoField]
      }
      if (yHiField) {
        point.yHi = r[yHiField]
      }

      if (linePropService.isTimeSeries(scaleTypeX)) {
        point.x = this.convertToDate(point.x)
      }

      s.points.push(point)

      s.bands = this.getBands(s.points, s.bandNames)

      return series
    }, [])

    this.sortSeries(series, strata.legend)
    series.forEach((s) => {
      s.label = strata.legendLabelMap[s.name] ?? s.label
    })

    return series
  }

  /**
   * Map from the raw data format into the format required by the table behind the line chart
   */
  toTableData (
    data,
    dataDefinition = {},
    strata,
    tableDefinition,
    { xFormat, timeFormat, scaleTypeX }
  ) {
    const rowField = dataDefinition.xField
    const formatType = linePropService.isTimeSeries(scaleTypeX) ? 'date' : xFormat

    data = data.map((d) => {
      const column = d[strata.value]
      const columnName = column ? strata.legendLabelMap[column] || column : column
      const dataPoint = {
        row: lineFormatService.formatValue(d[rowField], { formatType, timeFormat }),
        column:
            strata.value === null || strata.value === dataDefinition.emptyValue
              ? 'Overall'
              : columnName,
        ...d
      }
      return dataPoint
    })

    const config = {
      cellFormats: { default: tableDefinition.cellFormat },
      primaryLabel: tableDefinition.props.primaryLabel,
      columns: strata?.legend?.map(({ label }) => ({ value: label }))
    }

    return mapFromRawData(data, config)
  }

  convertToDate (value) {
    return moment.utc(value).toDate()
  }

  getMergedSeries (chartData = [], strata) {
    const merged = chartData.reduce((merged, seriesList) => {
      seriesList.forEach((series) => {
        if (!merged.find((s) => s.id === series.id)) {
          merged.push({
            id: series.id,
            name: series.name,
            label: series.label
          })
        }
      })

      return merged
    }, [])

    this.sortSeries(merged, strata?.legend)
    return merged
  }

  /**
   * Filter out the series that are toggled off
   */
  filterToggledChartSeries (chartData, legendToggles) {
    return chartData.filter((series) => !legendToggles[series.name] && !legendToggles[series.label])
  }

  /**
   * Filter out the columns that are toggled off
   */
  filterToggledTableColumns (tableData, legendToggles) {
    if (!tableData) {
      return tableData
    }
    tableData = { ...tableData }
    tableData.columns = tableData.columns?.filter((column) => !legendToggles[column.key] && !legendToggles[column.label])
    tableData.rows = tableData.rows?.map((row) => {
      row = { ...row }
      row.columns = row.columns?.filter((column) => !legendToggles[column.key] && !legendToggles[column.label])
      return row
    })
    return tableData
  }

  /**
   * Tranform the yLo, yHi, yLo2, yHi2, etc form of bands into an array of bands with names and colors
   *
   * @param {object[]} points Array of points with y, yLo, yHi, yLo2, yHi2, etc values
   * @param {string[]} bandNames Array of band names that correspond to the lo/hi pairs
   */
  getBands (points, bandNames) {
    const bands = []
    const getPointValue = (point, propName, bandNum) => point[`${propName}${bandNum > 1 ? bandNum : ''}`]

    const bandCount = points.reduce((bandCount, point) => {
      while (true) {
        const propNames = ['yLo', 'yHi']
        // If there is a lo or hi value for the next band, increment and keep looking
        if (propNames.find((propName) => getPointValue(point, propName, bandCount + 1) !== undefined)) {
          bandCount++
        } else {
          break
        }
      }
      return bandCount
    }, 0)

    for (let i = 1; i <= bandCount; i++) {
      bands.push({
        name: (bandNames && bandNames.length > (i - 1)) ? bandNames[i - 1] : `Band ${i}`,
        points: points.map((point) => {
          const y0 = getPointValue(point, 'yLo', i)
          const y1 = getPointValue(point, 'yHi', i)
          return {
            x: point.x,
            y0: (y0 !== undefined) ? y0 : point.y,
            y1: (y1 !== undefined) ? y1 : point.y
          }
        })
      })
    }

    return bands
  }
}

export const lineDataService = new LineDataService()
